import React from 'react';
import { Link } from 'react-router-dom';
import { useT } from '../i18n';
import BRAND from '../brand';
import { GUIDE_HELP, GUIDE_INTRO, GUIDE_PROBLEMS, GUIDE_STEPS } from '../i18n/selfHostedGuide';
import XpnsrPageHeader from './XpnsrPageHeader';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ArrowLeft, LifeBuoy, Wrench } from 'lucide-react';

function Code({ children }) {
  return (
    <pre className="mt-2 overflow-x-auto rounded-md border border-border bg-muted/40 px-3 py-2 font-mono text-xs">
      {children}
    </pre>
  );
}

/*
  Enterprise: how a customer installs the tracker on their own server without
  us. The install command itself (with their key) is on the Self-hosted page.
*/
export default function SelfHostedGuide() {
  const { t } = useT();
  return (
    <div className="px-8 py-10 max-w-4xl" data-testid="self-hosted-guide">
      <Button asChild variant="ghost" size="sm" className="mb-4 -ml-2">
        <Link to="/self-hosted">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> {t('Back to Self-hosted')}
        </Link>
      </Button>
      <XpnsrPageHeader kicker={t('Enterprise')} title={t('Install guide')} subtitle={t(GUIDE_INTRO)} />
      <div className="mb-6" />

      <ol className="space-y-4">
        {GUIDE_STEPS.map((step, i) => (
          <li key={step.title}>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-3 text-base">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/15 text-sm font-semibold text-primary">
                    {i + 1}
                  </span>
                  {t(step.title)}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {step.items.map((item) => (
                  <div key={item.text}>
                    <p className="text-muted-foreground">{t(item.text)}</p>
                    {item.code ? <Code>{item.code}</Code> : null}
                  </div>
                ))}
                {i === 2 || i === 4 ? (
                  <Button asChild size="sm" variant="outline">
                    <Link to="/self-hosted">{t('Self-hosted')}</Link>
                  </Button>
                ) : null}
              </CardContent>
            </Card>
          </li>
        ))}
      </ol>

      <Card className="mt-8">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Wrench className="w-4 h-4" /> {t('If something goes wrong')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="space-y-4 text-sm">
            {GUIDE_PROBLEMS.map((p) => (
              <div key={p.q}>
                <dt className="font-medium">{t(p.q)}</dt>
                <dd className="mt-1 text-muted-foreground">
                  {t(p.a)}
                  {p.code ? <Code>{p.code}</Code> : null}
                </dd>
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>

      <Card className="mt-6 border-primary/40">
        <CardContent className="py-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
          <div className="flex items-start gap-3 text-sm">
            <LifeBuoy className="w-4 h-4 mt-0.5 text-primary shrink-0" />
            <div>
              <p className="font-medium">{t('Prefer that we do it?')}</p>
              <p className="mt-1 text-muted-foreground">{t(GUIDE_HELP)}</p>
            </div>
          </div>
          <Button asChild variant="outline">
            <a href={'mailto:' + BRAND.supportEmail}>{t('Write to us')}</a>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
